// Key detection for arrays of objects — so JSON/YAML arrays match by KEY (like
// CSV rows), not by position. Shared by the engine (json-diff) and the UI
// (the "matched by" selector) so both agree on the key and the columns.
import { flatten } from './flatten.js';

// Common id-ish field names, in preference order.
const KEY_CANDIDATES = ['id', 'key', 'uuid', '_id', 'pk', 'sku', 'code', 'slug', 'name', 'email'];

/** A non-empty array whose every element is a plain (non-array) object. */
export function isArrayOfObjects(v) {
  return Array.isArray(v) && v.length > 0 && v.every((el) => el && typeof el === 'object' && !Array.isArray(el));
}

/** Does a record-set have two records sharing a key? (Key matching would then
 *  silently drop one, so the caller should warn + fall back to positional.) */
export function hasDuplicateKeys(recordSet) {
  const seen = new Set();
  for (const r of recordSet.records) {
    if (seen.has(r.key)) return true;
    seen.add(r.key);
  }
  return false;
}

/** Union of TOP-LEVEL keys across the elements — the candidate key columns. */
export function topLevelColumns(arr) {
  const s = new Set();
  for (const el of arr) if (el && typeof el === 'object' && !Array.isArray(el)) for (const k of Object.keys(el)) s.add(k);
  return [...s];
}

/** Union of flattened leaf paths across the elements — the display columns. */
export function recordColumns(arr) {
  const s = new Set();
  for (const el of arr) for (const k of Object.keys(flatten(el))) s.add(k);
  return [...s];
}

/**
 * Choose a key column from top-level columns: an explicit override wins, then a
 * known id-ish name, then any `*_id` / `*Id` / `*uuid` / `*key` suffix. Returns
 * null when nothing looks like a key (caller falls back to positional).
 */
export function pickKeyColumn(columns, override) {
  if (override && columns.includes(override)) return override;
  const lower = new Map(columns.map((c) => [c.toLowerCase(), c]));
  for (const cand of KEY_CANDIDATES) if (lower.has(cand)) return lower.get(cand);
  // Suffix match, two spellings: delimiter-separated (`order_id`, `user.key`)
  // and camelCase (`userId`, `accountUuid`, `orderID`). The camelCase pattern is
  // deliberately case-SENSITIVE — a case-insensitive letter boundary would also
  // claim ordinary words ending in "id" such as `grid`, `valid` or `paid`
  // (REL-059).
  for (const c of columns) {
    if (/(^|[._-])(id|uuid|key)$/i.test(c)) return c;
    if (/[a-z0-9](Id|ID|Uuid|UUID|Key|KEY)$/.test(c)) return c;
  }
  return null;
}
